import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocationContext } from '../../contexts/LocationContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useCMS } from '../../contexts/CMSContext';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { useEmailSettings } from '../../hooks/useEmailSettings';
import { EmailTemplate, getAvailableVariableSchemas, TemplateVariableSchema, sendTestEmail, getRandomReservation, getRandomCustomer, getRandomMarina, getEmailSettings, EmailSettings } from '../../firebase/services/emailService';
import { getTransactionalEmailSettings, TransactionalEmailSettings } from '../../firebase/services/transactionalEmailTemplateService';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  FormControl,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import {
  TextFields as TextIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  ViewModule as DividerIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  SpaceBar as SpaceBarIcon,
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { styled } from '@mui/material/styles';
import EmailTemplateEditor from './EmailTemplateEditor';
import ImageSelectorDialog from './ImageSelectorDialog';
import TiptapEmailEditor from './TiptapEmailEditor';
import { useEditor } from '@tiptap/react';

interface EmailComponent {
  id: string;
  type: 'text' | 'image' | 'button' | 'divider' | 'html' | 'spacer' | 'header' | 'footer' | 'headline' | 'gif' | 'video';
  content: string;
  styles?: Record<string, string>;
  settings?: {
    backgroundColor?: string;
    textColor?: string;
    fontSize?: string;
    fontFamily?: string;
    padding?: string;
    alignment?: 'left' | 'center' | 'right';
    borderRadius?: string;
    border?: string;
  };
}

interface EmailTemplateDesignerProps {
  template: EmailTemplate;
  onSave: (template: EmailTemplate) => void;
  onDelete: () => void;
  onContentChange?: (content: string) => void;
}

// Styled components for the drag items
const DragItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'grab',
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const SortableComponent: React.FC<{
  component: EmailComponent;
  index: number;
  updateComponent: (index: number, component: EmailComponent) => void;
  removeComponent: (index: number) => void;
  variables: string[];
  isSelected?: boolean;
  onSelect?: () => void;
  setFocusedField: (field: 'subject' | 'header' | 'footer' | 'text' | null) => void;
  setFocusedTextComponentId: (id: string | null) => void;
}> = ({ 
  component, 
  index, 
  updateComponent, 
  removeComponent, 
  variables, 
  isSelected = false, 
  onSelect,
  setFocusedField,
  setFocusedTextComponentId
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: component.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1000 : 1,
  };

  // Handle save for rich text editor
  const handleSaveRichText = (content: string) => {
    updateComponent(index, { ...component, content });
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ 
        position: 'relative',
        mb: 1
      }}
    >
      {/* Drag handle */}
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1 },
          display: 'flex',
          gap: 0.5,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 1,
          padding: '2px',
          zIndex: 5,
          cursor: 'grab'
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" color="action" />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s',
          display: 'flex',
          gap: 0.5,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 1,
          padding: '2px',
          zIndex: 5
        }}
      >
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            removeComponent(index);
          }}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* Basic component rendering */}
      {component.type === 'text' && (
        <Box onClick={() => {
          if (onSelect) onSelect();
          setFocusedField('text');
          setFocusedTextComponentId(component.id);
        }} sx={{ border: isSelected ? '2px solid #1976d2' : '1px solid transparent', borderRadius: 1, mb: 1, p: 1 }}>
          <EmailTemplateEditor
            value={component.content}
            onChange={(content) => handleSaveRichText(content)}
          />
        </Box>
      )}
      
      {component.type === 'image' && component.content && (
        <Box sx={{ textAlign: 'center' }}>
          <img 
            src={component.content} 
            alt="" 
            style={{ 
              maxWidth: '100%',
              borderRadius: component.settings?.borderRadius
            }} 
          />
        </Box>
      )}
      
      {component.type === 'button' && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: component.settings?.backgroundColor || '#1976d2',
              color: component.settings?.textColor || 'white',
              borderRadius: component.settings?.borderRadius || '4px'
            }}
          >
            {component.content || 'Button Text'}
          </Button>
        </Box>
      )}
      
      {component.type === 'divider' && <Divider sx={{ my: 2 }} />}
      
      {component.type === 'spacer' && (
        <Box sx={{ height: component.styles?.height || '20px' }} />
      )}
    </Box>
  );
};

const EmailTemplateDesigner: React.FC<EmailTemplateDesignerProps> = ({
  template,
  onSave,
  onDelete,
  onContentChange
}) => {
  const [components, setComponents] = useState<EmailComponent[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState(template.name);
  const [templateSubject, setTemplateSubject] = useState(template.subject);
  const [templateVariables, setTemplateVariables] = useState<string[]>(template.variables || []);
  const [variableSchemas, setVariableSchemas] = useState<TemplateVariableSchema[]>([]);
  const [notification, setNotification] = useState<{ open: boolean, message: string, type: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    type: 'info'
  });
  const [showHeader, setShowHeader] = useState(true);
  const [headerText, setHeaderText] = useState(templateName || 'Your Business Name');
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [headerBgColor, setHeaderBgColor] = useState('#f5f5f5');
  const [headerTextColor, setHeaderTextColor] = useState('#1976d2');
  const [headerLayout, setHeaderLayout] = useState<'logo-top' | 'logo-left' | 'text-only'>('text-only');
  const [showFooter, setShowFooter] = useState(true);
  const [footerText, setFooterText] = useState(`© ${new Date().getFullYear()} ${templateName || 'Your Business'}. All rights reserved.`);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [focusedField, setFocusedField] = useState<'subject' | 'header' | 'footer' | 'text' | null>(null);
  const [focusedTextComponentId, setFocusedTextComponentId] = useState<string | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const footerInputRef = useRef<HTMLInputElement>(null);
  const [editorHtml, setEditorHtml] = useState<string>(template.body || '');
  const tiptapEditorRef = useRef<any>(null);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  const { location } = useLocationContext();
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [emailSettings, setEmailSettings] = useState<TransactionalEmailSettings | null>(null);
  const [editorMode, setEditorMode] = useState<'wysiwyg' | 'code'>('wysiwyg');

  // Fetch variable schemas
  useEffect(() => {
    const fetchVariableSchemas = async () => {
      try {
        const schemas = await getAvailableVariableSchemas();
        setVariableSchemas(schemas);
      } catch (error) {
        console.error('Error fetching variable schemas:', error);
        showNotification('Failed to load variable schemas', 'error');
      }
    };

    fetchVariableSchemas();
  }, []);

  useEffect(() => {
    if (location?.id) {
      getTransactionalEmailSettings(location.id).then(setEmailSettings);
    }
  }, [location?.id]);

  // Update header logo when email settings change
  useEffect(() => {
    if (emailSettings?.brandingLogoUrl) {
      setHeaderLogo(emailSettings.brandingLogoUrl);
    }
  }, [emailSettings?.brandingLogoUrl]);

  // Initialize components from template body
  useEffect(() => {
    if (template.body) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = template.body;
        
        // Create simple components from HTML
        const newComponents: EmailComponent[] = [];
        Array.from(tempDiv.children).forEach((element, i) => {
          const el = element as HTMLElement;
          
          if (el.tagName === 'P' || el.tagName === 'DIV') {
            newComponents.push({
              id: `text-${i}`,
              type: 'text',
              content: el.innerHTML,
              settings: {
                textColor: el.style.color || undefined,
                backgroundColor: el.style.backgroundColor || undefined,
                fontSize: el.style.fontSize || undefined
              }
            });
          } else if (el.tagName === 'IMG') {
            const imgEl = el as HTMLImageElement;
            newComponents.push({
              id: `image-${i}`,
              type: 'image',
              content: imgEl.src || ''
            });
          } else if (el.tagName === 'HR') {
            newComponents.push({
              id: `divider-${i}`,
              type: 'divider',
              content: ''
            });
          }
        });
        
        setComponents(newComponents.length > 0 ? newComponents : [{
          id: 'default-text',
          type: 'text',
          content: '<span>Start creating your email template here!</span>'
        }]);
      } catch (error) {
        console.error('Error parsing template body:', error);
        setComponents([{
          id: 'default-text',
          type: 'text',
          content: '<span>Start creating your email template here!</span>'
        }]);
      }
    } else {
      setComponents([{
        id: 'default-text',
        type: 'text',
        content: '<span>Start creating your email template here!</span>'
      }]);
    }
  }, [template.body]);

  // Fetch random data for preview when entering preview mode
  useEffect(() => {
    const fetchPreviewData = async () => {
      if (!previewMode || !location?.id) return;
      const [reservation, customer, marina] = await Promise.all([
        getRandomReservation(location.id),
        getRandomCustomer(location.id),
        getRandomMarina(location.id)
      ]);
      const variableMap: Record<string, string> = {};
      if (reservation) {
        Object.entries(reservation).forEach(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number') {
            variableMap[key] = String(value);
          }
        });
      }
      if (customer) {
        Object.entries(customer).forEach(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number') {
            variableMap[key] = String(value);
          }
        });
      }
      if (marina) {
        Object.entries(marina).forEach(([key, value]) => {
          if (typeof value === 'string' || typeof value === 'number') {
            variableMap[key] = String(value);
          }
        });
      }
      setPreviewVariables(variableMap);
    };
    fetchPreviewData();
    // Only run when previewMode or location.id changes
  }, [previewMode, location?.id]);

  // Helper to replace variables in a string for preview
  const replaceVariables = (str: string) => {
    if (!str) return '';
    return str.replace(/{{(\w+)}}/g, (_, v) => previewVariables[v] ?? `[${v}]`);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setComponents((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    
    setActiveId(null);
  };

  const updateComponent = (index: number, component: EmailComponent) => {
    const newComponents = [...components];
    newComponents[index] = component;
    setComponents(newComponents);
  };

  const removeComponent = (index: number) => {
    const newComponents = [...components];
    newComponents.splice(index, 1);
    setComponents(newComponents);
    setSelectedComponentId(null);
    showNotification('Block removed', 'success');
  };

  const findComponentIndex = (id: string): number => {
    return components.findIndex(component => component.id === id);
  };

  const removeSelectedComponent = () => {
    if (selectedComponentId) {
      const index = findComponentIndex(selectedComponentId);
      if (index !== -1) {
        removeComponent(index);
      }
    }
  };

  // Insert content into Tiptap editor
  const insertComponent = (type: string) => {
    if (!tiptapEditorRef.current) return;
    const editor = tiptapEditorRef.current;
    switch (type) {
      case 'text':
        editor.commands.insertContent('<p>Your text here</p>');
        break;
      case 'image':
        setImageDialogOpen(true);
        break;
      case 'button':
        editor.commands.insertContent('<a href="#" class="button" style="display: inline-block; padding: 10px 20px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px;">Click Me</a>');
        break;
      case 'divider':
        editor.commands.insertContent('<hr />');
        break;
      case 'spacer':
        editor.commands.insertContent('<div style="height: 20px;"></div>');
        break;
      default:
        break;
    }
  };

  // Handle image selection from CMS
  const handleImageSelect = (imageUrl: string) => {
    if (tiptapEditorRef.current && tiptapEditorRef.current.insertImage) {
      tiptapEditorRef.current.insertImage(imageUrl);
    }
    setImageDialogOpen(false);
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({
      open: true,
      message,
      type
    });
  };

  // Update Tiptap editor reference
  useEffect(() => {
    if (tiptapEditorRef.current) {
      console.log('Tiptap editor initialized with content:', editorHtml);
    }
  }, [tiptapEditorRef.current, editorHtml]);

  const handleSave = () => {
    try {
      console.log('handleSave called in EmailTemplateDesigner');
      console.log('Current editor HTML content:', editorHtml);
      
      const updatedTemplate = {
        ...template,
        name: templateName,
        subject: templateSubject,
        body: editorHtml,
        variables: templateVariables,
        updatedAt: Date.now()
      };
      console.log('Saving template from designer:', JSON.stringify(updatedTemplate, null, 2));
      onSave(updatedTemplate);
      showNotification('Template saved successfully', 'success');
    } catch (error) {
      console.error('Error saving template:', error);
      showNotification('Error saving template', 'error');
    }
  };

  // Keyboard handling for delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedComponentId && (e.key === 'Delete' || e.key === 'Backspace' && e.metaKey)) {
        e.preventDefault();
        removeSelectedComponent();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedComponentId]);

  // Helper function to render the header based on layout setting
  const renderHeader = (isMobile = false) => {
    if (!showHeader) return null;

    const headerContent = (
      <Box
        sx={{
          p: isMobile ? 1.5 : 2,
          mb: 2,
          bgcolor: headerBgColor,
          borderRadius: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {headerLayout === 'text-only' && (
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{
              color: headerTextColor,
              textAlign: 'center',
              m: 0
            }}
          >
            {headerText}
          </Typography>
        )}

        {headerLayout === 'logo-top' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {headerLogo && (
              <Box sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={headerLogo}
                  alt={`${headerText} Logo`}
                  style={{
                    maxWidth: isMobile ? '100px' : '150px',
                    maxHeight: isMobile ? '40px' : '60px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            )}
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                color: headerTextColor,
                textAlign: 'center',
                m: 0
              }}
            >
              {headerText}
            </Typography>
          </Box>
        )}

        {headerLayout === 'logo-left' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {headerLogo && (
              <Box sx={{ flexShrink: 0 }}>
                <img
                  src={headerLogo}
                  alt={`${headerText} Logo`}
                  style={{
                    maxWidth: isMobile ? '80px' : '120px',
                    maxHeight: isMobile ? '32px' : '48px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            )}
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                color: headerTextColor,
                flex: 1,
                m: 0
              }}
            >
              {headerText}
            </Typography>
          </Box>
        )}
      </Box>
    );

    return headerContent;
  };

  const insertVariableIntoField = (variable: string) => {
    if (!focusedField) {
      showNotification('Please click in a field first', 'info');
      return;
    }

    const variableName = `{{${variable}}}`;
    
    // Add to template variables if not already there
    if (!templateVariables.includes(variable)) {
      setTemplateVariables([...templateVariables, variable]);
    }

    switch (focusedField) {
      case 'subject': {
        if (subjectInputRef.current) {
          const input = subjectInputRef.current;
          const start = input.selectionStart || 0;
          const end = input.selectionEnd || 0;
          const value = templateSubject;
          
          const newValue = value.substring(0, start) + variableName + value.substring(end);
          setTemplateSubject(newValue);
          
          // Set cursor after variable
          setTimeout(() => {
            const newPos = start + variableName.length;
            input.focus();
            input.setSelectionRange(newPos, newPos);
          }, 10);
        }
        break;
      }
      case 'header': {
        if (headerInputRef.current) {
          const input = headerInputRef.current;
          const start = input.selectionStart || 0;
          const end = input.selectionEnd || 0;
          const value = headerText;
          
          const newValue = value.substring(0, start) + variableName + value.substring(end);
          setHeaderText(newValue);
          
          // Set cursor after variable
          setTimeout(() => {
            const newPos = start + variableName.length;
            input.focus();
            input.setSelectionRange(newPos, newPos);
          }, 10);
        }
        break;
      }
      case 'footer': {
        if (footerInputRef.current) {
          const input = footerInputRef.current;
          const start = input.selectionStart || 0;
          const end = input.selectionEnd || 0;
          const value = footerText;
          
          const newValue = value.substring(0, start) + variableName + value.substring(end);
          setFooterText(newValue);
          
          // Set cursor after variable
          setTimeout(() => {
            const newPos = start + variableName.length;
            input.focus();
            input.setSelectionRange(newPos, newPos);
          }, 10);
        }
        break;
      }
      case 'text': {
        if (focusedTextComponentId) {
          const componentIndex = findComponentIndex(focusedTextComponentId);
          if (componentIndex !== -1 && components[componentIndex].type === 'text') {
            const component = components[componentIndex];
            const newContent = component.content + ` ${variableName}`;
            updateComponent(componentIndex, { ...component, content: newContent });
          }
        }
        break;
      }
    }
    
    showNotification(`Variable ${variableName} inserted`, 'success');
  };

  const handleSendTest = async () => {
    setTestEmailSending(true);
    setTestEmailError(null);
    try {
      if (!emailSettings) throw new Error('No email settings available');
      const result = await sendTestEmail(
        testEmailAddress,
        template.subject,
        template.body,
        previewVariables,
        {
          smtpHost: emailSettings.smtpHost || '',
          smtpPort: emailSettings.smtpPort || 587,
          smtpUsername: emailSettings.defaultSenderEmail || '',
          smtpPassword: '', // This should be handled securely
          fromEmail: emailSettings.defaultSenderEmail || '',
          fromName: emailSettings.defaultSenderName || '',
          enabled: true
        }
      );
      if (result.success) {
        showNotification('Test email sent successfully', 'success');
      } else {
        setTestEmailError(result.error || 'Failed to send test email');
      }
    } catch (err: any) {
      setTestEmailError(err.message || 'Failed to send test email');
    } finally {
      setTestEmailSending(false);
    }
  };

  // Update the preview mode rendering
  const renderPreviewContent = (isMobile = false) => (
    <Box sx={{ p: 3, bgcolor: 'white', minHeight: 300 }}>
      {showHeader && renderHeader(isMobile)}
      <Box>
        {components.map((component, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            {component.type === 'text' && (
              <Box dangerouslySetInnerHTML={{ __html: replaceVariables(component.content) }} />
            )}
            {component.type === 'image' && component.content && (
              <Box sx={{ textAlign: 'center' }}>
                <img src={component.content} alt="" style={{ maxWidth: '100%' }} />
              </Box>
            )}
            {component.type === 'button' && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: component.settings?.backgroundColor || '#1976d2',
                    color: component.settings?.textColor || 'white'
                  }}
                >
                  {component.content}
                </Button>
              </Box>
            )}
            {component.type === 'divider' && <Divider />}
            {component.type === 'spacer' && (
              <Box sx={{ height: component.styles?.height || '20px' }} />
            )}
          </Box>
        ))}
      </Box>
      {showFooter && (
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee', color: '#777', fontSize: '0.875rem', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {footerText}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Update the editor mode rendering
  const renderEditorContent = () => (
    <Box sx={{ p: 2 }}>
      {showHeader && renderHeader()}
      <Box mt={2}>
        {editorMode === 'wysiwyg' ? (
          <TiptapEmailEditor
            ref={tiptapEditorRef}
            value={editorHtml}
            onChange={setEditorHtml}
            variables={template.variables || []}
          />
        ) : (
          <TextField
            fullWidth
            multiline
            rows={20}
            value={editorHtml}
            onChange={(e) => setEditorHtml(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '14px'
              }
            }}
          />
        )}
      </Box>
      {showFooter && (
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee', color: '#777', fontSize: '0.875rem', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {footerText}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Add useEffect to sync editor content changes
  useEffect(() => {
    if (onContentChange) {
      onContentChange(editorHtml);
    }
  }, [editorHtml, onContentChange]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Top Bar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 1, 
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onDelete} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            {previewMode ? 'Email Preview' : 'Design Your Campaign'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {!previewMode && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={editorMode === 'code'}
                    onChange={(e) => setEditorMode(e.target.checked ? 'code' : 'wysiwyg')}
                    size="small"
                  />
                }
                label="Code Editor"
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPreviewMode(!previewMode)}
            startIcon={previewMode ? <EditIcon /> : <PreviewIcon />}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
        </Box>
      </Box>
      {/* Main Content Area */}
      {previewMode ? (
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', height: '100%', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {previewDevice === 'desktop' ? (
            <Box sx={{ 
              maxWidth: 800, 
              width: '100%',
              mx: 'auto',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2), 0 0 0 2px #333, 0 0 0 8px #eee',
              overflow: 'hidden',
              bgcolor: '#f5f5f5',
              position: 'relative'
            }}>
              {/* Monitor Top Bar */}
              <Box sx={{ 
                bgcolor: '#333', 
                color: 'white', 
                p: 1, 
                display: 'flex', 
                justifyContent: 'space-between',
                borderBottom: '1px solid #222'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: '#ff5f57', 
                    mr: 0.5 
                  }} />
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: '#ffbd2e', 
                    mr: 0.5 
                  }} />
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: '#28c940', 
                    mr: 1 
                  }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    Mail - Inbox
                  </Typography>
                </Box>
              </Box>

              {/* Email Client UI */}
              <Box sx={{ bgcolor: 'white', height: '100%' }}>
                {/* Email Client Toolbar */}
                <Box sx={{ 
                  bgcolor: '#f0f0f0', 
                  p: 1, 
                  borderBottom: '1px solid #ddd',
                  display: 'flex',
                  gap: 1
                }}>
                  <Box sx={{ 
                    bgcolor: '#e0e0e0', 
                    color: '#555', 
                    borderRadius: '4px', 
                    px: 1.5, 
                    py: 0.5, 
                    fontSize: '0.75rem', 
                    fontWeight: 500 
                  }}>
                    Reply
                  </Box>
                  <Box sx={{ 
                    bgcolor: '#e0e0e0', 
                    color: '#555', 
                    borderRadius: '4px', 
                    px: 1.5, 
                    py: 0.5, 
                    fontSize: '0.75rem', 
                    fontWeight: 500 
                  }}>
                    Forward
                  </Box>
                  <Box sx={{ 
                    bgcolor: '#e0e0e0', 
                    color: '#555', 
                    borderRadius: '4px', 
                    px: 1.5, 
                    py: 0.5, 
                    fontSize: '0.75rem', 
                    fontWeight: 500 
                  }}>
                    Delete
                  </Box>
                </Box>
                
                {/* Email Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {replaceVariables(templateSubject)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: '50%', 
                      bgcolor: '#0072c6', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 1.5
                    }}>
                      FM
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        Fallen Leaf Marina &lt;info@fallenleafmarina.com&gt;
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        to me
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.75rem' }}>
                      {new Date().toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        hour: 'numeric', 
                        minute: 'numeric',
                        hour12: true 
                      })}
                    </Box>
                  </Box>
                </Box>
                
                {/* Email Body with Content */}
                {renderPreviewContent()}
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              width: 375, 
              height: 667,
              mx: 'auto',
              borderRadius: '36px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              border: '14px solid #111',
              position: 'relative',
              bgcolor: '#111',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Phone Notch */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '150px',
                height: '28px',
                bgcolor: '#111',
                borderBottomLeftRadius: '14px',
                borderBottomRightRadius: '14px',
                zIndex: 10
              }}/>
              
              {/* Status Bar */}
              <Box sx={{ 
                bgcolor: '#333', 
                color: 'white', 
                p: 1, 
                pt: 3,
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '0.75rem'
              }}>
                <Typography variant="caption">9:41 AM</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption">LTE</Typography>
                  <Typography variant="caption">100%</Typography>
                </Box>
              </Box>

              {/* Mail App Header */}
              <Box sx={{ 
                bgcolor: '#f8f8f8', 
                p: 1.5, 
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="body2" fontWeight="medium">Inbox</Typography>
                <Typography variant="body2" color="primary">Edit</Typography>
              </Box>
              
              {/* Email Content */}
              <Box sx={{ 
                bgcolor: 'white', 
                flex: 1,
                overflow: 'auto'
              }}>
                {/* Email Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {replaceVariables(templateSubject)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      bgcolor: '#0072c6', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      mr: 1.5,
                      fontSize: '0.75rem'
                    }}>
                      FM
                    </Box>
                    <Box>
                      <Typography variant="caption" fontWeight="500">
                        Fallen Leaf Marina
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Yesterday, 4:30 PM
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Email Body */}
                {renderPreviewContent(true)}
              </Box>
              
              {/* Bottom Bar with Home Indicator */}
              <Box sx={{ 
                height: '34px',
                bgcolor: '#f8f8f8',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderTop: '1px solid #ddd'
              }}>
                <Box sx={{ width: '134px', height: '5px', bgcolor: '#999', borderRadius: '10px' }} />
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        // Editor mode content
        (<Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            {/* Email Canvas */}
            <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto', bgcolor: '#f5f5f5' }}>
              <Box sx={{ maxWidth: 800, mx: 'auto', bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
                {/* Email Subject */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <TextField
                    label="Email Subject"
                    fullWidth
                    size="small"
                    value={templateSubject}
                    onChange={(e) => setTemplateSubject(e.target.value)}
                    onFocus={() => setFocusedField('subject')}
                    inputRef={subjectInputRef}
                  />
                </Box>
                
                {/* Email Content */}
                {renderEditorContent()}
              </Box>
            </Box>

            {/* Right Sidebar - Components Panel Only */}
            <Box 
              sx={{ 
                width: 280, 
                borderLeft: '1px solid #e0e0e0', 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Header Settings */}
              <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Header Settings</Typography>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={showHeader} 
                          onChange={(e) => setShowHeader(e.target.checked)} 
                          color="primary" 
                          size="small"
                        />
                      }
                      label="Show Header"
                      sx={{ m: 0 }}
                    />
                  </Box>
                  
                  {showHeader && (
                    <>
                      <TextField
                        label="Header Text"
                        size="small"
                        fullWidth
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        onFocus={() => setFocusedField('header')}
                        inputRef={headerInputRef}
                        sx={{ mb: 2 }}
                      />
                      
                      <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>Logo</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Button 
                          variant="outlined" 
                          startIcon={<ImageIcon />}
                          onClick={() => setImageDialogOpen(true)}
                          size="small"
                        >
                          Select Logo
                        </Button>
                        {headerLogo && (
                          <Button 
                            variant="outlined" 
                            color="error" 
                            size="small"
                            onClick={() => setHeaderLogo('')}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                      
                      {headerLogo && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                          <img 
                            src={headerLogo} 
                            alt="Logo preview" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '60px',
                              marginBottom: '8px'
                            }} 
                          />
                          <Typography variant="caption" display="block" color="text.secondary">
                            Logo Preview
                          </Typography>
                        </Box>
                      )}
                      
                      <Typography variant="body2" gutterBottom>Layout</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Select
                          fullWidth
                          size="small"
                          value={headerLayout}
                          onChange={(e) => setHeaderLayout(e.target.value as 'logo-top' | 'logo-left' | 'text-only')}
                        >
                          <MenuItem value="text-only">Text Only</MenuItem>
                          <MenuItem value="logo-top">Logo Above Text</MenuItem>
                          <MenuItem value="logo-left">Logo Left of Text</MenuItem>
                        </Select>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" gutterBottom>Background Color</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={headerBgColor}
                            onChange={(e) => setHeaderBgColor(e.target.value)}
                            type="color"
                            sx={{ 
                              '& input': { padding: '8px 5px' }
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" gutterBottom>Text Color</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={headerTextColor}
                            onChange={(e) => setHeaderTextColor(e.target.value)}
                            type="color"
                            sx={{ 
                              '& input': { padding: '8px 5px' }
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
              
              {/* Component Panel */}
              <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" gutterBottom>
                  Email Components
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Add components to your email:
                </Typography>
                
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <DragItem 
                      elevation={0}
                      onClick={() => insertComponent('text')}
                    >
                      <TextIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Text</Typography>
                    </DragItem>
                  </Grid>
                  <Grid item xs={6}>
                    <DragItem 
                      elevation={0}
                      onClick={() => insertComponent('image')}
                    >
                      <ImageIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Image</Typography>
                    </DragItem>
                  </Grid>
                  <Grid item xs={6}>
                    <DragItem 
                      elevation={0}
                      onClick={() => insertComponent('button')}
                    >
                      <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Button</Typography>
                    </DragItem>
                  </Grid>
                  <Grid item xs={6}>
                    <DragItem 
                      elevation={0}
                      onClick={() => insertComponent('divider')}
                    >
                      <DividerIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Divider</Typography>
                    </DragItem>
                  </Grid>
                  <Grid item xs={6}>
                    <DragItem 
                      elevation={0}
                      onClick={() => insertComponent('spacer')}
                    >
                      <SpaceBarIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Spacer</Typography>
                    </DragItem>
                  </Grid>
                </Grid>
              </Box>
              
              {/* Footer Settings - Moved below components */}
              <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Footer Settings</Typography>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={showFooter} 
                          onChange={(e) => setShowFooter(e.target.checked)} 
                          color="primary" 
                          size="small"
                        />
                      }
                      label="Show Footer"
                      sx={{ m: 0 }}
                    />
                  </Box>
                  
                  {showFooter && (
                    <TextField
                      label="Footer Text"
                      size="small"
                      fullWidth
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      onFocus={() => setFocusedField('footer')}
                      inputRef={footerInputRef}
                      sx={{ mb: 2 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {/* Variables Section - Now at Bottom */}
          <Box 
            sx={{ 
              borderTop: '1px solid #e0e0e0', 
              p: 2,
              bgcolor: '#f8f8f8',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              maxHeight: '200px'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Available Variables
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click a variable to insert it at your cursor position
              </Typography>
            </Box>
            
            <Box sx={{ overflow: 'auto' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {variableSchemas.map((schema) => (
                  <Box key={schema.name} sx={{ mb: 1, mr: 2 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.main', display: 'block' }}>
                      {schema.name.charAt(0).toUpperCase() + schema.name.slice(1)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {schema.variables.map((variable) => (
                        <Chip
                          key={variable.name}
                          label={`{{${variable.name}}}`}
                          size="small"
                          onClick={() => {
                            insertVariableIntoField(variable.name);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText'
                            }
                          }}
                          title={variable.description}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
                
                {variableSchemas.length === 0 && (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No variable schemas available
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>)
      )}
      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({...notification, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={notification.type} 
          variant="filled"
          onClose={() => setNotification({...notification, open: false})}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      {/* Image Selector Dialog for CMS */}
      <ImageSelectorDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        onSelect={handleImageSelect}
        title="Select Image"
        allowUpload={true}
      />
      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onClose={() => setTestEmailDialogOpen(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Test Email Address"
              fullWidth
              type="email"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              error={!!testEmailError}
              helperText={testEmailError}
              disabled={testEmailSending}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will send a test email with sample data for all variables.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailDialogOpen(false)} disabled={testEmailSending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendTest} 
            variant="contained" 
            disabled={testEmailSending}
          >
            {testEmailSending ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateDesigner; 